import { X } from 'lucide-react';
import { useEffect } from 'react';
import { announce } from '../state/announcer.ts';
import { Button } from './Button.tsx';
import styles from './Toast.module.css';

export interface ToastProps {
  /** The same message announced politely through the shared announcer. */
  message: string;
  onDismiss: () => void;
  /** Dismiss auto-show after this delay; announcements are not repeated here. */
  autoDismissMs?: number | null;
}

/**
 * Non-modal toast: steals no focus, is dismissible, creates no live region of
 * its own — the message goes through the shared announce() so the one polite
 * LiveRegion stays the only announcer (§4.1 Toast).
 */
export function Toast({ message, onDismiss, autoDismissMs = null }: ToastProps) {
  useEffect(() => {
    announce(message);
  }, [message]);
  useEffect(() => {
    if (autoDismissMs === null) return;
    const t = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onDismiss]);

  return (
    <div className={styles.toast} role="presentation">
      <p className={styles.text}>{message}</p>
      <Button
        variant="tertiary"
        size="small"
        onPress={onDismiss}
        aria-label="Dismiss notification"
        className={styles.close}
      >
        <X size={16} aria-hidden="true" />
      </Button>
    </div>
  );
}
